"use client";

import { useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { person, routes, social } from "@/resources";
import { BookCall } from "./BookCall";
import { Clock } from "./Clock";
import { ThemeToggle } from "./ThemeToggle";
import styles from "./Header.module.scss";

/* The nav is the page's grid ruler: the wordmark owns the left half, the
   links spread edge-to-edge across the right half, so the first link marks
   the 50% seam that section heads and split layouts align to. */
const NAV_LINKS: {
  href: string;
  route: string;
  label: string;
  match: (pathname: string) => boolean;
}[] = [
  {
    href: "/work",
    route: "/work",
    label: "Work",
    match: (p) => p.startsWith("/work") || p.startsWith("/lab") || p.startsWith("/university"),
  },
  {
    href: "/blog",
    route: "/blog",
    label: "Writing",
    match: (p) => p.startsWith("/blog"),
  },
  {
    href: "/about",
    route: "/about",
    label: "About",
    match: (p) => p === "/about",
  },
];

/** Contact opens a hairline index of channels instead of jumping to mailto.
    Open state lives in Header: on mobile the panel is a full-width sheet
    under the header, which needs its canvas floor while the sheet is out. */
const ContactMenu = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onOpenChange(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  const linkedin = social.find((item) => item.name === "LinkedIn")?.link;
  const channelCount = linkedin ? 3 : 2;

  return (
    <div className={styles.contactWrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.link}${open ? ` ${styles.isActive}` : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          const next = !open;
          onOpenChange(next);
          if (next) posthog.capture("contact_panel_opened");
        }}
      >
        Contact
      </button>
      {/* inert (not aria-hidden alone) keeps the closed panel out of both
          the focus order and the accessibility tree, so the tree stays
          well-formed for AT and agent browsers */}
      <div className={`${styles.panel}${open ? ` ${styles.panelOpen}` : ""}`} inert={!open}>
        <div className={styles.panelHead}>
          <span className="oi-xs oi-faint">Contact / {String(channelCount).padStart(2, "0")}</span>
          <Clock timeZone={person.location} city="Antwerp" />
        </div>
        <a
          className={styles.panelItem}
          href={`mailto:${person.email}`}
          onClick={() => posthog.capture("email_link_clicked", { source: "contact_panel" })}
        >
          <span>Email</span>
          <span className={styles.panelMeta}>{person.email}</span>
        </a>
        {linkedin && (
          <a className={styles.panelItem} href={linkedin} target="_blank" rel="noreferrer">
            <span>LinkedIn</span>
            <span className={styles.panelMeta}>{"↗︎"}</span>
          </a>
        )}
        <BookCall className={styles.panelCta}>
          <span>Book a call</span>
          <span className={styles.ctaMeta}>
            <span className={styles.dot} aria-hidden />
            30 min
          </span>
        </BookCall>
      </div>
    </div>
  );
};

export const Header = () => {
  const pathname = usePathname() ?? "";
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 4);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`${styles.header}${scrolled ? ` ${styles.isScrolled}` : ""}${contactOpen ? ` ${styles.contactOpen}` : ""}`}
    >
      <Link
        href="/"
        className={styles.wordmark}
        aria-current={pathname === "/" ? "page" : undefined}
      >
        {/* On phones the full name is dead weight in the nav — the word-
            mark reads as plain "Home" there (CSS swaps the spans). */}
        <span className={styles.wordmarkName}>{person.name}</span>
        <span className={styles.wordmarkHome}>Home</span>
        <span className={styles.role}>{person.role}</span>
      </Link>
      <nav className={styles.links} aria-label="Primary">
        {NAV_LINKS.filter((link) => routes[link.route as keyof typeof routes]).map((link) => (
          <Link
            key={link.label}
            href={link.href}
            className={`${styles.link}${link.match(pathname) ? ` ${styles.isActive}` : ""}`}
            aria-current={link.match(pathname) ? "page" : undefined}
          >
            {link.label}
          </Link>
        ))}
        <ContactMenu open={contactOpen} onOpenChange={setContactOpen} />
        <ThemeToggle />
      </nav>
    </header>
  );
};
